/*
 * @group data
 */
import isNil from 'lodash/isNil';
import { types as t, getEnv, Instance, effect, reaction$, action as mstAction } from 'mst-effect';
import { cancelable } from 'cancelable-promise';
import { map } from 'rxjs';
import { dispatchChangeIndexEvent } from 'modules/InputViewer/utils/changeIndexEvent';
import {
  Input,
  Concept,
  Filter,
  Metadata,
  Rank,
  Query,
  Search,
  BoundingBox,
  RegionInfo,
  Region,
  Data,
  Annotation,
  Workflow,
} from 'api/grpc/resources';
import { either, taskEither } from 'fp-ts';
import { SingleInputResponse } from 'api/grpc/service';
import Router from 'next/router';
import { pipe, mapOr } from 'utils/fp';
import { splitEvery } from 'utils/collections';
import toastMessage from 'components/ToastMessage/ToastMessage';
import { getInputPagedIndex, getPageNumberByIndex } from 'modules/InputViewer/utils/helpers';
import { fetchSearchFromStorage } from 'utils/storage/inputsearch';
import { getModelVersionTE } from 'api/models/getModelVersionTE';
import {
  chunkInputIds,
  compileAPIFilters,
  convertConceptToNotTrainedSmartMenuItem,
  convertConceptToSmartMenuItem,
  delay,
} from 'modules/InputsManager/helpers';
import { AnnotationStatus } from 'modules/Annotations/store/annotationsStore';
import { getFiltersFromSearch, getFinalRanks } from 'modules/InputsManager/helpers/inputListStoreHelpers';
import { PER_PAGE, THRESHOLD } from 'modules/InputViewer/utils/constants';
import {
  SearchBarState,
  ZoomLevel,
  LayoutMode,
  LOCAL_STORAGE_LAYOUT_MODE,
  LOCAL_STORAGE_ZOOM_LEVEL,
  MULTIMODAL_EMBEDDER,
  TEXT_EMBEDDER,
} from 'modules/InputsManager/types';
import { InputsListStore } from 'modules/InputsManager/models/InputsListStore';
import { InputsStore } from 'modules/InputsManager/models/InputsStore';
import { FiltersStore } from 'modules/InputsManager/models/FiltersStore';
import { InputsViewerStore } from 'modules/InputsManager/models/InputsViewerStore';
import { fetchInputsCounts } from 'api/inputs/fetchInputsCounts';
import { AnnotationWithInput, AnnotationsListStore } from 'modules/InputsManager/models/AnnotationsListStore';
import { withRunInAction } from 'utils/withRunInAction';
import { fetchUserProfileTE } from 'api/users';
import { isRight } from 'fp-ts/lib/Either';
import { createAnnotationWithRegionAndConcept } from 'modules/InputsManager/helpers/annotationsListStoreHelpers';
import { cookieClientStorage } from 'utils/cookieStorage';

const INPUT_BATCH_SIZE = 128;
const DELETE_BATCH_SIZE = 25;

export enum WorkflowStages {
  STARTED = 'STARTED',
  ENDED = 'ENDED',
  FAILED = 'FAILED',
}

enum ModelIds {
  BaaiBgeBaseEnV15 = 'BAAI-bge-base-en-v15',
}

const AsymmetricalSearchPrompt = t.model({
  selected: t.boolean,
  value: t.string,
});

const InputsManagerSchema = {
  // Generic
  userOrOrgId: t.maybe(t.string),
  defaultWorkflow: t.maybeNull(t.frozen<Workflow>()),
  isWorkflowLoading: t.boolean,
  workflowStage: t.optional(
    t.enumeration<WorkflowStages>([WorkflowStages.STARTED, WorkflowStages.ENDED, WorkflowStages.FAILED]),
    WorkflowStages.STARTED,
  ),
  appId: t.maybe(t.string),
  isLoading: t.boolean,
  error: t.maybe(t.frozen<Error>()),
  delayedReload: t.optional(t.boolean, false),
  inputCounts: t.maybe(t.number),
  showInputsCounts: t.optional(t.boolean, false),
  // This map is intended for mapping jobs to uploaded inputs. After processing a job, the inputs from all jobs
  // will be stored in the same store. This brings up the possibility of wrongly assuming that a job has finished,
  // by counting the inputs from another job that finished first. By creating this map, we will avoid that problem.
  jobInputMap: t.map(t.array(t.string)),
  isDefaultWorkflowLoaded: t.optional(t.boolean, false),
  asymmetricalSearchPrompt: AsymmetricalSearchPrompt,
  patKey: t.maybe(t.string),
  scrollPosition: t.optional(t.number, 0),
};

const InputsManagerModel = t
  .compose(
    withRunInAction(),
    InputsListStore,
    InputsStore,
    AnnotationsListStore,
    FiltersStore,
    InputsViewerStore,
    t.model('InputManagerStore', InputsManagerSchema),
  )
  .views((self) => ({
    get env() {
      return getEnv<CF.MSTEnvironment>(self);
    },
    get errorText(): string {
      return self.error ? `${self.error.name}\n${self.error.message}` : '';
    },
    get hasAppParams(): boolean {
      return self.userOrOrgId !== undefined && self.appId !== undefined;
    },
  }))
  .actions((self) => ({
    setInputDeletionProgress(status: boolean) {
      self.isInputDeletionInProgress = status;
    },
  }))
  .actions((self) => ({
    setDefaultWorkflow(workflow: Workflow) {
      self.defaultWorkflow = workflow;
    },
    setInputCounts(count: number | undefined) {
      self.inputCounts = count;
    },
    setAppParams(userOrOrgId: string, appId: string) {
      self.userOrOrgId = userOrOrgId;
      self.appId = appId;
    },
    clearAppParams() {
      self.userOrOrgId = undefined;
      self.appId = undefined;
    },
    addJobInputEntry(jobId: string, inputIds: string[]) {
      self.jobInputMap.set(jobId, inputIds);
    },
    deleteJobInputEntry(jobId: string) {
      self.jobInputMap.delete(jobId);
    },
    didIndexReachEnd(index: number, isAnnotationsModeActive: boolean = false): boolean {
      const page = getPageNumberByIndex(index);
      if (isAnnotationsModeActive) {
        return self.hasAnnotationsListReachedEnd && self.lastAnnotationsListPage && page ? page >= self.lastAnnotationsListPage : false;
      }
      return self.hasReachedEnd && self.lastPage && page ? page >= self.lastPage : false;
    },
    setIsLoading(isLoading: typeof self.isLoading) {
      self.isLoading = isLoading;
    },
    setError(error: typeof self.error) {
      self.error = error;
    },
    setIsDefaultWorkflowLoaded(flag: typeof self.isDefaultWorkflowLoaded) {
      self.isDefaultWorkflowLoaded = flag;
    },
    setPatKey(value: string) {
      self.patKey = value;
    },
  }))
  .actions((self) => ({
    finishLoadingWithError(e: Error) {
      self.setError(e);
      self.setIsLoading(false);
      self.setRankSearchInProcess(false);
    },
    finishLoadingWithData() {
      self.setError(undefined);
      self.setIsLoading(false);
    },
    setShowInputsCounts(value: boolean) {
      self.showInputsCounts = value;
    },
    setIsWorkflowLoading(value: boolean) {
      self.isWorkflowLoading = value;
    },
    setIsWorkflowStage(value: WorkflowStages) {
      self.workflowStage = value;
    },
  }))
  .actions((self) => ({
    async getDefaultWorkflow() {
      const { grpc: grpcClient } = self.env;
      const { userOrOrgId, appId, defaultWorkflow, isWorkflowLoading } = self;
      if (isWorkflowLoading || defaultWorkflow || !userOrOrgId || !appId) return;
      self.setIsWorkflowLoading(true);
      self.setIsWorkflowStage(WorkflowStages.STARTED);
      const resp = await grpcClient.getWorkflow({ userOrOrgId, appId });
      pipe(
        resp,
        either.match(
          (e) => {
            self.setIsWorkflowStage(WorkflowStages.FAILED);
            console.warn('Cannot load default workflow', e);
          },
          (workflowResponse) => {
            const workflow = workflowResponse.getWorkflow();
            self.setIsWorkflowStage(WorkflowStages.ENDED);
            if (workflow) self.setDefaultWorkflow(workflow);
          },
        ),
      );
      self.setIsWorkflowLoading(false);
    },
  }))
  .actions((self) => {
    effect<string>(self, () =>
      reaction$(() => self.isLoading, { fireImmediately: false }).pipe(
        map(() =>
          mstAction(() => {
            if (self.workflowStage === WorkflowStages.FAILED && !self.isLoading) {
              self.setIsWorkflowStage(WorkflowStages.STARTED);
            }
          }),
        ),
      ),
    );
    return {};
  })
  .actions((self) => ({
    async fetchCounts(filters: Filter[], ranks: Rank[]) {
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      const counts = await fetchInputsCounts({ userOrOrgId, appId, filters, ranks });
      const countsString = counts?.hit_counts[0]?.estimated_total;
      if (countsString && countsString.length > 0) {
        self.setInputCounts(Number(countsString));
      } else {
        self.setInputCounts(0);
      }
    },
    setCurrentIndex(index: number) {
      self.currentIndex = index;
      self.updateCurrentInput(index);
      dispatchChangeIndexEvent(index);
    },
  }))
  .actions((self) => ({
    async fetchPage(page: number, clearAllBeforeFetch: boolean = false, clearAllAfterFetch: boolean = false) {
      if (self.pagesStartedLoad.includes(page)) {
        // Page load in progress, skip
        self.setRankSearchInProcess(false);
        return;
      }
      self.setIsLoading(true);

      if (clearAllBeforeFetch) self.clearAllInputsRecords();

      self.pagesStartedLoad.push(page);
      // TODO: check if this is not breaking last page fetch
      if (self.hasReachedEnd && self.lastPage && page >= self.lastPage) {
        console.warn('tried to fetch last page');
        return;
      }

      // TODO: we need to remove it, most likely, but we missing searchId in the store
      const { router } = Router;
      const { grpc: grpcClient } = self.env;
      const { userOrOrgId, appId, searchId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      const search = await fetchSearchFromStorage(searchId);

      const {
        metadataFilter,
        unlabeledFilter,
        unassignedFilter,
        conceptFilters,
        datasetFilters,
        conceptsFilterOperator,
        inputTypeFilters,
        statusFilterValue,
        labelerFilterValue,
      } = self;

      const isAnnotationsMode = false; // Because PostInputsSearches is only for Inputs Mode, not for Annotations Mode

      // PostInputSearches API does not support Annotation Filter with status filter of 'Success'
      const isStatusFilterEqualsToAnnotation = search.statusFilterValue !== AnnotationStatus.Any;

      const filters = searchId
        ? getFiltersFromSearch(search, isAnnotationsMode, isStatusFilterEqualsToAnnotation)
        : compileAPIFilters({
            metadataFilter,
            unlabeledFilter,
            unassignedFilter,
            conceptFilters,
            datasetFilters,
            inputTypeFilters,
            conceptsFilterOperator,
            allSearch: true,
            statusFilterValue,
            labelerFilterValue,
            allowStatusFilterForAnnotation: false,
          });

      const defaultWorkflowId = self.defaultWorkflow?.getId();

      const isTextWorkflowWithAsymmetricalSearchPrompt = defaultWorkflowId === 'Text' && self.asymmetricalSearchPrompt.value;

      const ranks = await getFinalRanks(search, self.ranks, self.asymmetricalSearchPrompt, router?.query);

      const resp = await grpcClient.postInputsSearches({ userOrOrgId, appId, page, perPage: PER_PAGE, filters, ranks });

      // Fetch total counts only for the first page
      if (self.showInputsCounts && page === 1) {
        self.fetchCounts(filters, ranks);
      }

      pipe(
        resp,
        either.match(
          (e) => self.finishLoadingWithError(e),
          () => {
            const arePageEqual = self.arePagesEqual(page, resp);
            const areScoresEqual = isTextWorkflowWithAsymmetricalSearchPrompt ? self.areScoresEqual(resp) : true;

            if (!arePageEqual || !areScoresEqual) {
              if (clearAllAfterFetch) self.clearAllInputsRecords();
              self.setInputsListPage(page, resp);
            } else {
              self.checkIfReachEnd(resp);
            }
            // NOTE: temporary fix for sometimes showing "No inputs" after switching between inputs/annotation mode
            // runInAction will not help here as stores divided into two different files (sub-stores)
            setTimeout(() => {
              self.setIsLoading(false);
              self.setRankSearchInProcess(false);
            }, 100);
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    async fetchAllCollaborators() {
      const { router } = Router;
      const { grpc: grpcClient } = self.env;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string };
      let page = 1;
      let hasMorePages = true;
      const allCollaborators = [];

      while (hasMorePages) {
        const collaboratorListResponse = await grpcClient.listCollaborators({
          userOrOrgId,
          appId,
          page,
          perPage: INPUT_BATCH_SIZE,
          listAllCollaborators: true,
        });

        if (collaboratorListResponse && isRight(collaboratorListResponse)) {
          const response = collaboratorListResponse.right;
          const collaborators = response.getCollaboratorsList();
          if (collaborators.length > 0) {
            allCollaborators.push(
              ...collaborators
                .map((collaborator) => {
                  const user = collaborator.getUser();
                  return user
                    ? {
                        userId: user.getId(),
                        firstName: user.getFirstName(),
                        lastName: user.getLastName(),
                      }
                    : null;
                })
                .filter(Boolean),
            );

            if (collaborators.length === INPUT_BATCH_SIZE) {
              page += 1;
            } else {
              hasMorePages = false;
            }
          } else {
            hasMorePages = false;
          }
        } else {
          hasMorePages = false;
          console.error('Error while fetching collaborators', collaboratorListResponse?.left);
        }
      }

      return allCollaborators;
    },
  }))
  .actions((self) => ({
    async getAllCollaboratorsList() {
      const sessionToken = cookieClientStorage.get('session_token');

      const { router } = Router;
      const { userOrOrgId } = router?.query as { userOrOrgId: string; appId: string };
      const allCollaborators = await self.fetchAllCollaborators();
      allCollaborators.forEach((collaborator) => {
        if (collaborator) {
          self.runInAction(() => {
            self.collaboratorsListMap.set(String(collaborator?.userId), collaborator);
          });
        }
      });
      const appOwnerResponse = await pipe(
        await fetchUserProfileTE({ userId: userOrOrgId, sessionToken }),
        taskEither.chain((response) => taskEither.right(response)),
        taskEither.fold(
          (error) => taskEither.left(error),
          (response) => taskEither.right(response.user),
        ),
      )();

      pipe(
        appOwnerResponse,
        either.match(
          (e) => {
            console.error('Error while fetching app owner', e);
            return null;
          },
          () => {
            // Merging appOwner response into usersListMap
            if (isRight(appOwnerResponse)) {
              const user = appOwnerResponse.right;
              self.runInAction(() => {
                self.collaboratorsListMap.set(String(user.id), {
                  userId: user.id,
                  firstName: user.first_name ?? '',
                  lastName: user.last_name ?? '',
                });
              });
            }
          },
        ),
      );
    },
  }))
  .actions((self) => ({
    async loadCollobratorsInAsyc() {
      // Check if listCollaborators has already been called to prevent multiple calls
      if (!self.isCollaboratorsListLoaded && !self.isCollaboratorsListLoading) {
        self.runInAction(() => {
          self.isCollaboratorsListLoading = true;
        });
        await self.getAllCollaboratorsList();
        self.runInAction(() => {
          self.isCollaboratorsListLoaded = true;
        });
      }
    },
  }))
  .actions((self) => ({
    async fetchAnnotationsPage(page: number, clearAllBeforeFetch: boolean = false, clearAllAfterFetch: boolean = false) {
      self.loadCollobratorsInAsyc();
      if (self.annotationsPagesStartedLoad.includes(page)) {
        // Page load in progress, skip
        self.setRankSearchInProcess(false);
        return;
      }

      if (clearAllBeforeFetch) self.clearAllAnnotationsRecords();

      self.annotationsPagesStartedLoad.push(page);

      if (self.hasAnnotationsListReachedEnd && self.lastAnnotationsListPage && page >= self.lastAnnotationsListPage) {
        console.warn('tried to fetch last annotations list page');
        return;
      }

      const { router } = Router;
      const { grpc: grpcClient } = self.env;
      const { userOrOrgId, appId, searchId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      const storedSearchId = await fetchSearchFromStorage(searchId);

      const {
        unlabeledFilter,
        unassignedFilter,
        conceptFilters,
        datasetFilters,
        conceptsFilterOperator,
        inputTypeFilters,
        statusFilterValue,
        labelerFilterValue,
      } = self;
      const filters = searchId
        ? getFiltersFromSearch(storedSearchId, true)
        : compileAPIFilters({
            unlabeledFilter,
            unassignedFilter,
            conceptFilters,
            datasetFilters,
            inputTypeFilters,
            conceptsFilterOperator,
            statusFilterValue,
            labelerFilterValue,
          });

      const ranks = await getFinalRanks(storedSearchId, self.ranks, self.asymmetricalSearchPrompt, router?.query);

      const boundingBox = new BoundingBox();
      const regionInfo = new RegionInfo();
      regionInfo.setBoundingBox(boundingBox);
      const region = new Region();
      region.setRegionInfo(regionInfo);

      const annotationData = new Data();
      annotationData.addRegions(region);

      const annotation = new Annotation();
      annotation.setData(annotationData);

      const filter = new Filter();
      filter.setAnnotation(annotation);
      const isAnnotationSuccess = statusFilterValue === AnnotationStatus.Success;
      const trustedAnnotation = new Annotation();
      const trustedAnnotationfilter = new Filter();
      trustedAnnotation.setTrusted(true);
      trustedAnnotationfilter.setAnnotation(trustedAnnotation);

      const query = new Query();

      // pass trusted filter only when statusFilterValue is 'success'
      const filterList = isAnnotationSuccess ? [trustedAnnotationfilter, ...filters, filter] : [...filters, filter];

      query.setFiltersList(filterList);

      // Note. Remove when visual search (rank) is applied
      // when doing rank search - disable delete, edit bar (bottom), add concept is good to have (check if it works though)
      if (ranks.length || datasetFilters.length) {
        // TODO: Add ranks and filters (another task)
        query.setRanksList(ranks);

        const search = new Search();
        search.setQuery(query);

        const resp = await grpcClient.postAnnotationsSearches({ userOrOrgId, appId, page, perPage: PER_PAGE, searches: [search] });
        // TODO: Add fetch count (another task)

        pipe(
          resp,
          either.match(
            (e) => self.finishLoadingAnnotationsWithError(e),
            () => {
              const areAnnotationsPagesEqual = self.areAnnotationsPagesEqual(page, resp);

              if (!areAnnotationsPagesEqual) {
                if (clearAllAfterFetch) self.clearAllAnnotationsRecords();
                self.setAnnotationsListPage(page, resp);
              } else {
                self.checkIfReachEnd(resp);
              }
              self.setRankSearchInProcess(false);
              self.setIsLoading(false);
            },
          ),
        );
      } else {
        const search = new Search();
        search.setQuery(query);
        const resp = await grpcClient.postAnnotationsSearches({ userOrOrgId, appId, page, perPage: PER_PAGE, searches: [search] });

        pipe(
          resp,
          either.match(
            (e) => self.finishLoadingAnnotationsWithError(e),
            () => {
              const areAnnotationsPagesEqual = self.areAnnotationsPagesEqual(page, resp);

              if (!areAnnotationsPagesEqual) {
                if (clearAllAfterFetch) self.clearAllAnnotationsRecords();
                self.setAnnotationsListPage(page, resp);
              } else {
                self.checkIfReachEnd(resp);
              }
              self.setRankSearchInProcess(false);
              self.setIsLoading(false);
            },
          ),
        );
      }
    },
  }))
  .actions((self) => ({
    selectAllAnnotations: () => {
      const length = self.annotationsListLength;
      const annotationIndices = Array.from({ length }).map((_item, index) => index);
      self.selections.replace(annotationIndices);
    },
    getSelectedAnnotationsList: () => {
      const result = [];
      for (const index of self.selections) {
        const annotation = self.getAnnotationObjectByIndex(index);
        if (annotation !== undefined) {
          result.push(annotation);
        }
      }
      return result;
    },
  }))
  .actions((self) => ({
    getAnnotationInputObjectByIndex(index: number): AnnotationWithInput | undefined {
      const pageNumber = getPageNumberByIndex(index);
      const inputPagedIndex = getInputPagedIndex(index);
      if (pageNumber !== undefined && inputPagedIndex !== undefined) {
        const page = self.annotationsListRecords.get(String(pageNumber));
        if (page) {
          return page[inputPagedIndex];
        }
      }

      return undefined;
    },
  }))
  .actions((self) => ({
    getSelectedAnnotationsWithInputList: () => {
      const result = [];
      for (const index of self.selections) {
        const annotationWithInput = self.getAnnotationInputObjectByIndex(index);
        if (annotationWithInput !== undefined) {
          result.push(annotationWithInput);
        }
      }
      return result;
    },
    setScrollPosition(position: number) {
      self.scrollPosition = position;
    },
    resetScrollPosition() {
      self.scrollPosition = 0;
    },
  }))
  .actions((self) => ({
    nextPage(isAnnotationsModeActive: boolean = false) {
      if (isAnnotationsModeActive) {
        const updatedPage = self?.currentAnnotationsListPage ? self.currentAnnotationsListPage + 1 : 1;
        self.currentAnnotationsListPage = updatedPage;
        self.fetchAnnotationsPage(updatedPage);
      } else {
        const updatedPage = self?.currentPage ? self.currentPage + 1 : 1;
        self.currentPage = updatedPage;
        self.fetchPage(updatedPage);
      }
    },
    async refetchInputs(shouldClearFilters: boolean = true, shouldClearInputs: boolean = true) {
      // reset all data
      self.hasReachedEnd = null;
      self.lastPage = null;
      self.currentPage = 1;
      self.currentInput = null;
      self.currentIndex = -1;
      self.pagesStartedLoad.clear();
      self.clearSelections();
      self.resetScrollPosition();
      if (shouldClearFilters) self.clearFilters();
      await self.fetchPage(1, false, shouldClearInputs);
    },

    async refetchAnnotations(shouldClearFilters: boolean = true) {
      // reset all data
      self.hasAnnotationsListReachedEnd = null;
      self.lastAnnotationsListPage = null;
      self.currentAnnotationsListPage = 1;
      self.annotationsPagesStartedLoad.clear();
      if (shouldClearFilters) self.clearFilters();
      self.clearAllAnnotationsRecords();
      await self.fetchAnnotationsPage(1, false, true); // clear all after fetch
    },

    async loadDefaultWorkflowId() {
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      if (!userOrOrgId || !appId) return;
      const appResponse = await self.env.grpc.getApp({ userOrOrgId, appId });
      if (either.isRight(appResponse)) {
        const baseWorkflow = appResponse.right.getApp()?.getDefaultWorkflow();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        self.setDefaultWorkflow(baseWorkflow!);
        self.setIsDefaultWorkflowLoaded(true);
      }
    },
    // TODO: make sure it is optimized for annotations
    async checkThreshold(index: number, isAnnotationsModeActive: boolean = false): Promise<void> {
      const hasReachedEnd = self.didIndexReachEnd(index, isAnnotationsModeActive);
      const minIndex = index - THRESHOLD;
      const currentItem = isAnnotationsModeActive ? self.getAnnotationInputObjectByIndex(index) : self.getInputObjectByIndex(index);

      if (!currentItem) {
        // fetch current page
        const currentPage = getPageNumberByIndex(index);
        if (currentPage !== undefined) {
          if (isAnnotationsModeActive) await self.fetchAnnotationsPage(currentPage);
          if (!isAnnotationsModeActive) await self.fetchPage(currentPage);
        }
      }

      if (minIndex >= 0) {
        // check if page exists

        const minIndexItem = isAnnotationsModeActive ? self.getAnnotationInputObjectByIndex(minIndex) : self.getInputObjectByIndex(minIndex);
        if (!minIndexItem) {
          // need to fetch
          const minPage = getPageNumberByIndex(minIndex);
          if (minPage !== undefined) {
            if (isAnnotationsModeActive) await self.fetchAnnotationsPage(minPage);
            if (!isAnnotationsModeActive) await self.fetchPage(minPage);
          }
        }
      }

      if (!hasReachedEnd) {
        const maxIndex = index + THRESHOLD;
        const maxIndexItem = isAnnotationsModeActive ? self.getAnnotationInputObjectByIndex(maxIndex) : self.getInputObjectByIndex(maxIndex);
        if (!maxIndexItem) {
          // need to fetch
          const maxPage = getPageNumberByIndex(maxIndex);
          if (maxPage !== undefined) {
            if (isAnnotationsModeActive) await self.fetchAnnotationsPage(maxPage);
            if (!isAnnotationsModeActive) await self.fetchPage(maxPage);
          }
        }
      }
    },
    setAsymmetricSearchPrompt(value: Partial<{ selected: boolean; value: string }>, isAnnotationsModeActive = false) {
      self.asymmetricalSearchPrompt = { ...self.asymmetricalSearchPrompt, ...value };

      if (isAnnotationsModeActive) {
        this.refetchAnnotations(false);
      } else {
        this.refetchInputs(false);
      }
    },
  }))
  .actions((self) => ({
    getAsymmetricSearchPrompt: async (isAnnotationsModeActive = false): Promise<void> => {
      const { userOrOrgId, appId } = self;
      const defaultWorkflowId = self.defaultWorkflow?.getId();
      const node = self.defaultWorkflow?.getNodesList()?.find((_) => _.getModel()?.getId() === ModelIds.BaaiBgeBaseEnV15);
      const modelVersionId = node?.getModel()?.getModelVersion()?.getId() as string;

      if (!self.defaultWorkflow || !userOrOrgId || !appId || !modelVersionId || defaultWorkflowId !== 'Text') return;

      const data = await getModelVersionTE({
        userOrOrgId,
        appId,
        modelId: ModelIds.BaaiBgeBaseEnV15,
        modelVersionId,
      })();
      if (isRight(data)) {
        const model = data.right;
        self.setAsymmetricSearchPrompt({ value: model.model_version.metadata.asymmetric_search_prompt as string }, isAnnotationsModeActive);
      }
    },
    addConceptsToAnnotationList: async (concepts: Concept[]) => {
      const selectedAnnotations = self.getSelectedAnnotationsList();
      const { userOrOrgId, appId } = self;

      if (!userOrOrgId || !appId || !selectedAnnotations) return;

      const annotationsList = concepts.flatMap((conceptItem) =>
        selectedAnnotations.map((annotationItem) =>
          createAnnotationWithRegionAndConcept(conceptItem, annotationItem as Annotation, userOrOrgId as string),
        ),
      );

      let errorOccurred = false;

      const setErrorOccurred = () => {
        errorOccurred = true;
      };

      const promises = [];
      for (let i = 0; i < annotationsList.length; i += INPUT_BATCH_SIZE) {
        const annotationsChunk = annotationsList.slice(i, i + INPUT_BATCH_SIZE);

        promises.push(
          self.env.grpc
            .postAnnotations({
              appId,
              userOrOrgId,
              annotations: annotationsChunk,
            })
            .then((response) => {
              return response;
            })
            .catch(setErrorOccurred),
        );
      }

      await Promise.all(promises);

      if (errorOccurred) {
        toastMessage.error('There was an error while adding the concept to an object.');
      } else {
        toastMessage.success('The concepts have been added successfully to the objects!');
        self.clearSelections();
        self.resetAnnotationsListStore();
        self.refetchAnnotations();
      }
    },
  }))
  .actions((self) => ({
    async removeSelectedAnnotations() {
      const selectedAnnotationsWithInput = self.getSelectedAnnotationsWithInputList();
      const { userOrOrgId, appId } = self;

      if (!userOrOrgId || !appId || !selectedAnnotationsWithInput) return;

      let errorOccurred = false;

      const setErrorOccurred = () => {
        errorOccurred = true;
      };
      self.setInputDeletionProgress(true);
      const promises = [];
      for (let i = 0; i < selectedAnnotationsWithInput.length; i += DELETE_BATCH_SIZE) {
        const annotationWithInputsChunk = selectedAnnotationsWithInput.slice(i, i + DELETE_BATCH_SIZE);
        const annotations = annotationWithInputsChunk.map((annotationWithInput) => {
          // creating annotation object with only id,inputId and data
          const annotation = new Annotation();
          annotation.setId(annotationWithInput.annotation.getId());
          annotation.setInputId(annotationWithInput.annotation.getInputId());
          annotation.setData(annotationWithInput.annotation.getData());
          return annotation;
        });
        promises.push(
          self.env.grpc
            .patchAnnotations({
              appId,
              userOrOrgId,
              annotations,
              action: 'remove',
              deleteIfEmptyData: true,
            })
            .then((response) => {
              return response;
            })
            .catch(setErrorOccurred),
        );
      }

      await Promise.all(promises);

      if (errorOccurred) {
        toastMessage.error('There was an error while deleting annotations.');
        self.setInputDeletionProgress(false);
      } else {
        toastMessage.success('Annotations have been successfully deleted!');
        self.setInputDeletionProgress(false);
        self.clearSelections();
        self.resetAnnotationsListStore();
        self.refetchAnnotations();
      }
    },
  }))

  .actions((self) => ({
    async getAnnotationListInputByIndex(index: number): Promise<Input | undefined> {
      await self.checkThreshold(index, true);
      return self.getAnnotationInputObjectByIndex(index)?.input;
    },
  }))
  .actions((self) => ({
    async getAnnotationListAnnotationByIndex(index: number): Promise<Annotation | undefined> {
      await self.checkThreshold(index, true);
      return self.getAnnotationInputObjectByIndex(index)?.annotation;
    },
  }))
  .actions((self) => ({
    async getInput(index: number) {
      // TODO used in 1 place, maybe replace or use in more places
      await self.checkThreshold(index);
      return self.getInputObjectByIndex(index);
    },
    setCurrentInput(inputObject: Input) {
      self.currentInput = inputObject;
    },
  }))
  .actions((self) => ({
    async fetchInputByInputId(inputId: string) {
      const { grpc } = self.env;
      const { userOrOrgId, appId } = self;
      if (!userOrOrgId || !appId) return null;
      self.setIsLoading(true);
      const result = await grpc.getInput({ userOrOrgId, appId, inputId });
      return pipe(
        result,
        either.match(
          (e: Error) => {
            self.finishLoadingWithError(e);
            return null;
          },
          (data: SingleInputResponse) => {
            const specificInput = data.getInput();
            self.finishLoadingWithData();
            return specificInput || null;
          },
        ),
      );
    },
  }))

  .actions((self) => ({
    async loadInputAnnotationViewerByInputId(inputId: string, currentIndex = -1) {
      //  Attempt to find the annotation in the existing cache
      let annotationDetails = self.getAnnotationObjectAndIndexById(inputId);
      if (!annotationDetails) {
        // If annotation are not found in the cache, then initiate an API call to load the first page
        await self.fetchAnnotationsPage(1);
        annotationDetails = self.getAnnotationObjectAndIndexById(inputId);
      }
      //  If annotation not found on the first page records
      if (annotationDetails) {
        let annotationIndex = annotationDetails.index;
        if (currentIndex > 0) {
          //  checking current index contains valid annotation comparing to input id
          const annotationFromIndex = await self.getAnnotationListInputByIndex(Number(currentIndex));
          if (inputId === annotationFromIndex?.getId()) {
            annotationIndex = currentIndex;
          }
        }
        self.setCurrentIndex(annotationIndex);
        self.setCurrentInput(annotationDetails.annotationWithInput.input);
        return { isInputFound: true, index: annotationIndex };
      }
      //  if input not valid fetching specif input
      const fetchedInput = await self.fetchInputByInputId(inputId);
      if (fetchedInput) {
        self.setCurrentInput(fetchedInput);
        return { isInputFound: true, index: -1 };
      }
      // if it is invalid input then return this
      return { isInputFound: false, index: -1 };
    },
    async loadInputViewerByInputId(inputId: string) {
      //  Attempt to find the input in the existing cache
      let inputDetails = self.getInputObjectAndIndexById(inputId);
      if (!inputDetails) {
        // If inputDetails are not found in the cache, then initiate an API call to load the first page
        await self.fetchPage(1, true, true);
        inputDetails = self.getInputObjectAndIndexById(inputId);
      }
      //  If input found on the first page records
      if (inputDetails) {
        self.setCurrentIndex(inputDetails.index);
        return { isInputFound: true, index: inputDetails.index };
      }
      const fetchedInput = await self.fetchInputByInputId(inputId);
      if (fetchedInput) {
        self.setCurrentInput(fetchedInput);
        return { isInputFound: true, index: -1 };
      }
      // if it is invalid input then return this
      return { isInputFound: false, index: -1 };
    },
  }))
  .actions((self) => ({
    async getInputInitially(index: number, inputId: string, isAnnotationsModeActive: boolean) {
      if (!isAnnotationsModeActive) {
        await self.getInput(index);
      } else {
        await self.getAnnotationListInputByIndex(index);
      }

      if (!isAnnotationsModeActive) {
        return self.loadInputViewerByInputId(inputId);
      } else {
        return self.loadInputAnnotationViewerByInputId(inputId, index);
      }
    },
  }))
  .actions((self) => ({
    async afterCreate() {
      await self.loadDefaultWorkflowId();
    },
  }));
/**
 * Compose the base InputsManager store with a listable store to take advantage of infinite scrolling
 */
// const makeInputsManagerModel = ({ userOrOrgId, appId, perPage }: { userOrOrgId: string; appId: string; perPage: number }) => {
const makeInputsManagerModel = () => {
  return InputsManagerModel.actions((self) => ({
    getSelectedInputIds(): string[] {
      const selections = self.selections.map((index) => self.getInputObjectByIndex(index)?.getId());
      return selections.filter((selection) => !!selection) as string[];
    },
  }))
    .actions((self) => ({
      waitUntilAllInputsAreDeleted: async (inputIds: string[]): Promise<boolean> => {
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;

        if (!userOrOrgId || !appId) {
          return Promise.reject(new Error('Missing user/org ID or App ID'));
        }

        const checkInputs = async (idsBatch: string[]): Promise<boolean> => {
          try {
            const grpcResponse = await grpc.listInputs({ userOrOrgId, appId, page: 0, perPage: INPUT_BATCH_SIZE, inputIds: idsBatch });
            const result = pipe(
              grpcResponse,
              either.match(
                (e: Error) => {
                  console.error('Error response received:', e);
                  throw new Error(`Error response received: ${e}`);
                },
                (response) => {
                  const inputsList = response.getInputsList();
                  const existingIds = inputsList.map((input) => input.getId());

                  return !idsBatch.some((id) => existingIds.includes(id));
                },
              ),
            );
            if (!result) {
              await delay(1000);
              return await checkInputs(idsBatch);
            }
            return result;
          } catch (error) {
            console.error('Error during polling:', error);
            return false;
          }
        };

        const inputIdChunks = chunkInputIds(inputIds, INPUT_BATCH_SIZE);
        try {
          for (const idsBatch of inputIdChunks) {
            await delay(1000);
            const batchCleared = await checkInputs(idsBatch);
            if (!batchCleared) {
              throw new Error('Failed to clear all input IDs in batch.');
            }
          }
          return true;
        } catch (error) {
          console.error('Error occurred:', error);
          throw error;
        }
      },
    }))
    .actions((self) => ({
      selectAllInputs() {
        const length = self.inputsLength;
        const inputIndices = Array.from({ length }).map((_item, index) => index);
        self.selections.replace(inputIndices);
      },
      reloadOrDelay() {
        if (self.selections.length === 0) {
          // TODO
          // self.load();
          self.delayedReload = false;
        } else {
          self.delayedReload = true;
        }
      },

      /**
       * Get Image Url by getting specific input from input ID
       * @param inputId
       * @returns Promise<string>
       */

      async getSpecificInput(inputId: string): Promise<Input | null> {
        const inputsList: Input[] = self.listItems;
        const foundInput: Input | undefined = inputsList.find((x) => x.getId() === inputId);

        if (foundInput) {
          return foundInput;
        } else {
          return self.fetchInputByInputId(inputId);
        }
      },
      /**
       * Delete all selected inputs
       */
      async deleteSelectedInputsOptimistically(): Promise<void> {
        // persist previous list values
        const previousListRecords = new Map(self.inputsListRecords);
        const previousSelections = [...self.selections] as number[];

        // Delete inputs locally
        const selectedInputIds = self.getSelectedInputIds();
        const inputsToDelete = [...selectedInputIds];
        const inputsList: Input[] = [] as Input[];
        const pagesToRefetch: string[] = [];
        const processedPages: string[] = [];

        // removing items from currently loaded inputsListRecords
        self.inputsListRecords.forEach((inputList: Input[], pageNumber) => {
          const isFirstPage = processedPages.length === 0 && pageNumber === '1';
          const isPageInSequence = processedPages.length + 1 === Number(pageNumber);
          if (isFirstPage || isPageInSequence) {
            inputList.forEach((input) => {
              if (!inputsToDelete.includes(input.getId())) {
                inputsList.push(input);
              }
            });
            processedPages.push(pageNumber);
          }
        });
        // rebuilding inputsListRecords
        self.setInputDeletionProgress(true);
        if (self.inputCounts === selectedInputIds.length) {
          self.clearAllInputsRecords();
        } else {
          processedPages.forEach((pageNumber: string) => {
            const startSlice = (Number(pageNumber) - 1) * PER_PAGE;
            const newPageList = inputsList.slice(startSlice, startSlice + PER_PAGE);
            self.updateInputListRecordItem(Number(pageNumber), newPageList);
            if (newPageList.length !== PER_PAGE) {
              pagesToRefetch.push(pageNumber);
            }
          });
        }

        self.clearSelections();

        toastMessage.success('Deleting inputs...');

        // Send request to delete inputs
        const deleteRequests = [];
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;
        try {
          for (const inputIds of splitEvery(INPUT_BATCH_SIZE)(inputsToDelete)) {
            deleteRequests.push(grpc.deleteInputs({ userOrOrgId, appId, inputIds }));
          }
          await Promise.allSettled(deleteRequests);
          try {
            await self.waitUntilAllInputsAreDeleted(selectedInputIds);
            toastMessage.success('Inputs has been deleted!');
          } catch {
            toastMessage.error('We are unable to verify that the inputs have been deleted.');
          }
          self.setInputDeletionProgress(false);
          self.resetInputsListStore();
          await self.refetchInputs();
        } catch {
          // onError: restore previous values and previous selection
          previousListRecords.forEach((inputList: Input[], pageNumber) => {
            self.updateInputListRecordItem(Number(pageNumber), inputList);
          });
          self.setInputsSelection(previousSelections);
          self.setInputDeletionProgress(false);
          toastMessage.error('Failed to delete inputs. Please try again.');
        }
      },

      deleteSelectedInputs(): void {
        const selectedInputIds = self.getSelectedInputIds();
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;

        for (const inputIds of splitEvery(INPUT_BATCH_SIZE)(selectedInputIds)) {
          grpc.deleteInputs({ userOrOrgId, appId, inputIds });
        }

        self.clearSelections();
      },

      async deleteSpecificInput(inputId: string) {
        let errorOccurred = false;
        const setErrorOccurred = () => {
          errorOccurred = true;
        };
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;
        await grpc.deleteInputs({ userOrOrgId, appId, inputIds: [inputId] }).catch(setErrorOccurred);

        if (errorOccurred) {
          toastMessage.error('There was an error while deleting an input.');
        } else {
          toastMessage.success('Input have been successfully deleted!');
          self.resetInputsListStore();
          await self.refetchInputs();
        }
      },
      async deleteSpecificAnnotation(annotation: Annotation) {
        if (annotation !== undefined) {
          let errorOccurred = false;

          const { grpc } = self.env;
          const { userOrOrgId, appId } = self;
          if (!userOrOrgId || !appId) return;
          const setErrorOccurred = () => {
            errorOccurred = true;
          };
          // creating annotation object with only id,inputId and data
          const _annotation = new Annotation();
          _annotation.setId(annotation.getId());
          _annotation.setInputId(annotation.getInputId());
          _annotation.setData(annotation.getData());

          await grpc
            .patchAnnotations({
              appId,
              userOrOrgId,
              action: 'remove',
              annotations: [_annotation],
              deleteIfEmptyData: true,
            })
            .catch(setErrorOccurred);

          if (errorOccurred) {
            toastMessage.error('There was an error while deleting an annotation.');
          } else {
            toastMessage.success('Annotation have been successfully deleted!');
            self.resetAnnotationsListStore();
            await self.refetchAnnotations();
          }
        }
      },
      /**
       * Toggle the selection of a range of inputs
       */
      rangeToggle(index: number) {
        // if there's no existing selection, toggle just the one that was clicked
        if (self.selections.length === 0) {
          self.selections.push(index);
        } else {
          const lastSelection = self.selections[self.selections.length - 1];
          const lower = Math.min(lastSelection, index);
          const upper = Math.max(lastSelection, index);
          const isIndexSelected = self.selections.includes(index);

          if (isIndexSelected) {
            const itemIndex = self.selections.indexOf(index);
            self.selections.splice(itemIndex);
          } else {
            for (let i = lower; i <= upper; i += 1) {
              if (!self.selections.includes(i)) self.selections.push(i);
            }
          }
        }
      },

      /**
       * Add or Remove labels or metadata to/from selected inputs
       */
      addorRemoveToSelection(action: CF.API.Action, allSearch: boolean, metadata: Metadata | undefined = undefined, concepts: Concept[] | undefined) {
        const selectedInputIds = self.getSelectedInputIds();
        const {
          metadataFilter,
          unlabeledFilter,
          unassignedFilter,
          conceptFilters,
          datasetFilters,
          conceptsFilterOperator,
          inputTypeFilters,
          statusFilterValue,
          labelerFilterValue,
        } = self;
        const { grpc } = self.env;

        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;

        const call: CF.API.PostBulkOperationCall = {
          userOrOrgId,
          appId,
          metadata,
          concepts,
          action,
        };
        if (allSearch) {
          call.filters = compileAPIFilters({
            metadataFilter,
            unlabeledFilter,
            unassignedFilter,
            conceptFilters,
            datasetFilters,
            inputTypeFilters,
            conceptsFilterOperator,
            allSearch,
            statusFilterValue,
            labelerFilterValue,
          });
        } else {
          call.inputIds = selectedInputIds;
        }

        grpc
          .postBulkOperations(call)
          .then((data) => {
            if (action === 'add') {
              toastMessage.success('The selected inputs have been successfully labeled.', { hideAfter: 2.5 });
            } else if (action === 'remove') {
              toastMessage.success('Labels have been successfully removed.', { hideAfter: 2.5 });
            }
            return data;
          })
          .catch(() => {
            toastMessage.error('An error occurred while labeling the inputs.', { hideAfter: 2.5 });
          });

        self.clearSelections();
      },

      /**
       * Add selected inputs to a dataset
       */
      addSelectedInputsToDatasets(datasetIds: string[], allSearch: boolean = false) {
        const selectedInputIds = self.getSelectedInputIds();
        const {
          metadataFilter,
          unlabeledFilter,
          unassignedFilter,
          conceptFilters,
          datasetFilters,
          conceptsFilterOperator,
          inputTypeFilters,
          statusFilterValue,
          labelerFilterValue,
        } = self;
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;

        for (const inputIds of splitEvery(INPUT_BATCH_SIZE)(selectedInputIds)) {
          for (const datasetId of datasetIds) {
            const call: {
              userOrOrgId: string;
              appId: string;
              inputIds?: string[];
              datasetId: string;
              filters?: Filter[];
            } = {
              userOrOrgId,
              appId,
              datasetId,
            };

            if (allSearch) {
              call.filters = compileAPIFilters({
                metadataFilter,
                unlabeledFilter,
                unassignedFilter,
                conceptFilters,
                datasetFilters,
                inputTypeFilters,
                conceptsFilterOperator,
                allSearch,
                statusFilterValue,
                labelerFilterValue,
              });
            } else {
              call.inputIds = inputIds;
            }

            grpc.postDatasetInputs(call);
          }
        }

        self.clearSelections();
      },

      /**
       * Remove selected inputs from selected datasets
       */
      removeSelectedInputsFromDatasets(datasetIds: string[], allSearch: boolean = false) {
        const selectedInputIds = self.getSelectedInputIds();
        const {
          metadataFilter,
          unlabeledFilter,
          unassignedFilter,
          conceptFilters,
          datasetFilters,
          conceptsFilterOperator,
          inputTypeFilters,
          statusFilterValue,
          labelerFilterValue,
        } = self;
        const { grpc } = self.env;
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return;

        for (const inputIds of splitEvery(INPUT_BATCH_SIZE)(selectedInputIds)) {
          for (const datasetId of datasetIds) {
            const call: {
              userOrOrgId: string;
              appId: string;
              inputIds: string[];
              datasetId: string;
              filters?: Filter[];
            } = {
              userOrOrgId,
              appId,
              inputIds,
              datasetId,
            };

            if (allSearch) {
              call.filters = compileAPIFilters({
                metadataFilter,
                unlabeledFilter,
                unassignedFilter,
                conceptFilters,
                datasetFilters,
                inputTypeFilters,
                conceptsFilterOperator,
                allSearch,
                statusFilterValue,
                labelerFilterValue,
              });
            }

            grpc.deleteDatasetInputs(call);
          }
        }

        self.clearSelections();
      },

      /**
       * Determines whether the app's baseworkflow has multimodal-embedder as model Id
       * @returns Promise<boolean>
       */
      async getBaseWorkflowHasMultimodalEmbedder() {
        const { userOrOrgId, appId } = self;
        if (!userOrOrgId || !appId) return undefined;
        if (self.defaultWorkflow) {
          const defaultWorkflowId = self.defaultWorkflow?.getId();
          if (!isNil(defaultWorkflowId)) {
            const nodes = self.defaultWorkflow.getNodesList().map((node) => ({
              modelTypeId: node?.getModel()?.getModelTypeId(),
              nodeInputs: node?.getNodeInputsList().map((n) => n.toObject()),
            }));

            if (nodes) {
              // Get all of the model ver ids, of base workflow and add in every request negate filter for each model ver id
              return !!nodes.some(
                (node) => (node.modelTypeId === MULTIMODAL_EMBEDDER || node.modelTypeId === TEXT_EMBEDDER) && node.nodeInputs.length === 0,
              );
            }
          }
        }
        return undefined;
      },
    }))
    .actions((self) => {
      function _trainedAppConceptsMatchingQuery(query: string): Concept[] {
        return self.trainedConcepts.filter((concept) => concept.getId().toLowerCase().includes(query.toLowerCase()));
      }
      return {
        async fetchConceptAutocompletions(query: string, page: number = 1, perPageValue: number = 25) {
          // Erase old promises
          if (self?.autocompletePromise?.cancel) {
            self.autocompletePromise.cancel();
          }
          const { env, defaultWorkflow } = self;
          const defaultWorkflowId = self.defaultWorkflow?.getId();
          const { userOrOrgId, appId } = self;
          if (!userOrOrgId || !appId) return {};

          const workflowAppId = defaultWorkflow?.getAppId() || appId;
          const workflowUserId = defaultWorkflow?.getUserId() || userOrOrgId;

          const call = {
            userOrOrgId: workflowUserId,
            appId: workflowAppId,
            workflowId: defaultWorkflowId,
            page,
            perPage: perPageValue,
            // We change dashes for wildcards to also match spaces. Check the ticket
            // https://clarifai.atlassian.net/browse/LT-3916 for more info.
            name: query.replace('-', '*'),
            includeMainAppConcepts: true,
            outsideApp: appId !== workflowAppId || userOrOrgId !== workflowUserId,
            fuzzy: true,
          };

          const requestCall = cancelable(env.grpc.postConceptsSearches(call));
          self.autocompletePromise = requestCall;
          const result = await requestCall;
          const concepts = mapOr(result, (resp) => resp.getConceptsList(), []);

          // get matching trained concepts
          const trainedConcepts = _trainedAppConceptsMatchingQuery(query);

          // add custom trained concepts to the top of the list
          for (const trainedConcept of trainedConcepts) {
            if (concepts.findIndex((concept) => concept.getId() === trainedConcept.getId()) === -1) {
              concepts.unshift(trainedConcept);
            }
          }

          // remove all concepts that are also in store.untrainedConcepts
          const untrainedConceptIds = self.untrainedConcepts.map((concept) => concept.getId());
          // TODO: MAKE THIS VALID
          // eslint-disable-next-line consistent-return
          return {
            values: concepts.filter((concept) => !untrainedConceptIds.includes(concept.getId())).map(convertConceptToSmartMenuItem),
            untrainedConcepts: concepts
              .filter((concept) => untrainedConceptIds.includes(concept.getId()))
              .map(convertConceptToNotTrainedSmartMenuItem),
            hasRequestReachedEnd: concepts?.length < perPageValue,
          };
        },
      };
    });
};

export type InputsManagerStore = Instance<ReturnType<typeof createInputManagerStore>>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createInputManagerStore(env: CF.MSTEnvironment, userOrOrgId?: string, appId?: string) {
  let layoutMode = LayoutMode.Grid;
  let zoomLevel = ZoomLevel.Default;

  if (typeof localStorage !== 'undefined') {
    layoutMode = (localStorage.getItem(LOCAL_STORAGE_LAYOUT_MODE) as LayoutMode) || LayoutMode.Grid;
    zoomLevel = (Number(localStorage.getItem(LOCAL_STORAGE_ZOOM_LEVEL)) as ZoomLevel) || ZoomLevel.Default;
  }

  const init = {
    userOrOrgId,
    appId,
    page: 1,
    perPage: PER_PAGE,
    isLoading: false,
    isWorkflowLoading: false,
    zoomLevel,
    layoutMode,
    datasets: [],
    concepts: [],
    datasetFilters: [],
    conceptFilters: [],
    inputTypeFilters: [],
    searchText: '',
    searchToken: [],
    searchBarInteractionState: SearchBarState.Editing,
    asymmetricalSearchPrompt: { selected: false, value: '' },
  };

  /**
   * Here we compose the base InputsManager store with a listable store to take advantage of infinite scrolling
   */
  const model = makeInputsManagerModel();
  return model.create(init, env);
}
